import { DeviceType } from 'src/types/DeviceType';
import { PulseMeterType } from 'src/types/DeviceTypeCapability';

/**
 * Mirror of api.tetherme.io's BulkDeviceProvision model (`main-api/src/models/BulkDeviceProvision.ts`),
 * served by `/microservice/bulk-device-provision`. tesseract declares API shapes locally — there is no
 * shared-models dependency in this repo.
 *
 * ONE record exists per uploaded batch; per-device outcomes live only in the result CSV in S3.
 */

export enum BulkDeviceProvisionStatus {
  Queued = 'QUEUED',
  Running = 'RUNNING',
  Completed = 'COMPLETED',
  CompletedWithErrors = 'COMPLETED_WITH_ERRORS',
  Failed = 'FAILED',
  Cancelled = 'CANCELLED',
}

/** Statuses the API still has work queued for — the UI polls while a batch is in one of these. */
export const ACTIVE_BULK_PROVISION_STATUSES: BulkDeviceProvisionStatus[] = [
  BulkDeviceProvisionStatus.Queued,
  BulkDeviceProvisionStatus.Running,
];

/** Statuses that can be re-queued from their cursor (completed rows are never redone). */
export const RETRYABLE_BULK_PROVISION_STATUSES: BulkDeviceProvisionStatus[] = [
  BulkDeviceProvisionStatus.Failed,
  BulkDeviceProvisionStatus.Cancelled,
  BulkDeviceProvisionStatus.CompletedWithErrors,
];

export interface BulkDeviceProvision {
  id: string;
  uploadId: string;
  fileName: string;
  deviceType: DeviceType;
  network: string;
  frequencyPlan: string;
  sku?: string | null;
  pulseMeterType?: PulseMeterType | null;
  status: BulkDeviceProvisionStatus;
  totalCount: number;
  successCount: number;
  failureCount: number;
  /** Rows that failed CSV validation and were never sent to provisioning. */
  skippedCount: number;
  /** Resume cursor — also the "rows processed so far" figure. */
  nextRowIndex: number;
  cancelRequestedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  error?: string | null;
  createdByAccessKeyId?: string | null;
  /** Human-readable actor (the access key's username), captured when the batch was created. */
  createdByLabel?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BulkProvisionValidationRow {
  index: number;
  /** 1-based line number in the uploaded file, for pointing the operator at the right row. */
  lineNumber: number;
  serialNumber: string;
  devEUI: string;
  appKey: string;
  appEUI: string;
  /** Present when the row cannot be provisioned — it will be skipped, not retried. */
  error?: string;
  /** Present when this device is already in the platform. Not an error: provisioning updates it. */
  existingDeviceId?: string;
}

export interface BulkProvisionValidation {
  uploadId: string;
  columns: string[];
  totalRows: number;
  validRows: number;
  invalidRows: number;
  existingDeviceRows: number;
  /** Error text → number of rows with it, so thousands of identical errors read as one line. */
  errorCounts: Record<string, number>;
  /** Capped preview (first 50 rows) — the full picture is in the result CSV. */
  rows: BulkProvisionValidationRow[];
}

/** POST / response: the created batch plus the validation summary it was created from. */
export interface CreatedBulkDeviceProvision extends BulkDeviceProvision {
  validation: BulkProvisionValidation;
}

export interface BulkDeviceProvisionList {
  items: BulkDeviceProvision[];
  total: number;
}

export interface BulkProvisionUploadTarget {
  uploadId: string;
  preSignedUrl: string;
}

export interface BulkProvisionResultDownload {
  url: string;
  fileName: string;
}

export interface CreateBulkDeviceProvisionRequest {
  uploadId: string;
  fileName: string;
  deviceType: DeviceType;
  network: string;
  frequencyPlan: string;
  sku?: string;
  pulseMeterType?: PulseMeterType;
}

/** Rows processed so far. Derived, never stored — mirrors how the API defines it. */
export const processedCount = (batch: BulkDeviceProvision): number =>
  batch.successCount + batch.failureCount + batch.skippedCount;

export const isBulkProvisionActive = (batch: BulkDeviceProvision): boolean =>
  ACTIVE_BULK_PROVISION_STATUSES.includes(batch.status);

/**
 * Mirrors the API's `LOCK_TAKEOVER_AFTER_MS`: a batch whose row has not been touched for this long has
 * no live runner behind it. `updatedAt` is the signal available to the client — a working runner
 * refreshes it every progress flush and every heartbeat.
 */
export const BULK_PROVISION_STALE_AFTER_MS = 3 * 60 * 1000;

/**
 * RUNNING, but stranded: its runner was killed mid-batch (a deploy, a lost host) and, because the
 * background runner is event-driven rather than polling, nothing will pick it up until the next upload
 * or deploy.
 */
/**
 * QUEUED but nobody is coming: the runner is event-driven, and its drain gives up after repeated errors —
 * the API's own retry now accepts exactly this state (`droppedBeforeStarting` in the bulk-provision
 * service) because nothing polls to pick it back up. Judged on `updatedAt` for the same reason the
 * RUNNING case is: a batch the wake claimed is set RUNNING within moments.
 */
export const isBulkProvisionDropped = (batch: BulkDeviceProvision, now: number = Date.now()): boolean =>
  batch.status === BulkDeviceProvisionStatus.Queued &&
  now - new Date(batch.updatedAt).getTime() > BULK_PROVISION_STALE_AFTER_MS;

export const isBulkProvisionStranded = (
  batch: BulkDeviceProvision,
  /**
   * Reference "now". Callers pass the newest SERVER timestamp they have seen (never earlier than the
   * local clock), because a workstation clock running behind the API would otherwise keep a genuinely
   * stranded batch out of reach — and retry is the only way to recover one. Erring the other way is
   * safe: the API re-checks staleness and rejects a retry on a live batch.
   */
  now: number = Date.now(),
): boolean =>
  batch.status === BulkDeviceProvisionStatus.Running &&
  now - new Date(batch.updatedAt).getTime() > BULK_PROVISION_STALE_AFTER_MS;

/**
 * A FAILED batch whose cursor already covers every row did all its work and published its result file —
 * only its status is wrong, because the finalisation threw after the last flush. The API's retry REPAIRS
 * that batch (bulkDeviceProvision/service.ts — it rewrites the status from the counts instead of
 * rejecting), so the button must be reachable for exactly the state that needs it. Requiring rows to
 * remain would hide the repair behind the one condition that defines it.
 */
export const isBulkProvisionRepairable = (batch: BulkDeviceProvision): boolean =>
  batch.status === BulkDeviceProvisionStatus.Failed && batch.nextRowIndex >= batch.totalCount;

/**
 * Retry covers the terminal statuses AND a stranded RUNNING batch — the API accepts both, and for a
 * stranded batch it is the operator's only way to restart the work, so the button has to be reachable.
 */
export const isBulkProvisionRetryable = (batch: BulkDeviceProvision, now?: number): boolean => {
  // Derived from the one action model, so a surface can never offer Retry for a state the API rejects.
  const { kind } = describeBulkProvisionAction(batch, now);
  return kind === 'resume' || kind === 'repair';
};

/**
 * What the operator can actually DO with this batch, in one place.
 *
 * Three review rounds produced five findings that were all the same mistake: each surface (the button
 * label, the FAILED callout, the stranded warning, the snackbar, the history row) decided independently
 * whether retrying would resume work, repair a status, or do nothing — and they disagreed. The worst
 * version told the operator to "use Retry" in the one state where no Retry button is rendered and the API
 * answers 400. So the decision is made ONCE here and every surface renders from it.
 *
 * The states, and why each answers as it does:
 * - `resume`  — rows remain and nobody owns the batch (terminal, or stranded RUNNING). Retry re-queues.
 * - `repair`  — FAILED with the cursor at the end: the work and the result file are complete and only the
 *               status is wrong. The API rewrites it; no device is touched.
 * - `finalise`— stranded RUNNING with the cursor at the end. The API accepts NEITHER retry (it repairs only
 *               a FAILED batch) nor a resume (no rows left), so Retry must not be offered or named. It
 *               self-heals: the next runner to start claims the stale lock and finalises it. Cancel also
 *               closes the record.
 * - `none`    — a live batch, or one that is already finished.
 */
export type BulkProvisionActionKind = 'resume' | 'repair' | 'finalise' | 'none';

export interface BulkProvisionAction {
  kind: BulkProvisionActionKind;
  /** Button label, when a button should be offered at all. */
  label?: string;
  /** One sentence the operator can act on. */
  explanation?: string;
}

export const describeBulkProvisionAction = (
  batch: BulkDeviceProvision,
  now?: number,
): BulkProvisionAction => {
  const rowsRemain = batch.nextRowIndex < batch.totalCount;
  const stranded = isBulkProvisionStranded(batch, now) || isBulkProvisionDropped(batch, now);

  if (isBulkProvisionRepairable(batch)) {
    return {
      kind: 'repair',
      label: 'Fix status (all rows already processed)',
      explanation:
        'Every row was already processed and the results CSV is complete — only this status is wrong. '
        + 'Fixing it corrects the record; no device is touched.',
    };
  }

  if (stranded && !rowsRemain) {
    return {
      kind: 'finalise',
      explanation:
        'Every row was already processed, but the batch was interrupted before it could record its final '
        + 'status. It will be closed automatically the next time a runner starts, or you can cancel it to '
        + 'close the record now. No device is affected either way.',
    };
  }

  if (rowsRemain && (RETRYABLE_BULK_PROVISION_STATUSES.includes(batch.status) || stranded)) {
    return {
      kind: 'resume',
      label: `Retry remaining ${(batch.totalCount - batch.nextRowIndex).toLocaleString()} rows`,
      explanation: `Retrying continues from row ${(batch.nextRowIndex + 1).toLocaleString()} — devices already provisioned are not touched again.`,
    };
  }

  return { kind: 'none' };
};
